<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Price from '../common/price.vue';
import AddCart from '../common/add-cart.vue';
import { MMContainer } from '@mm-frontend/mm-web-ui-kit';
import type { IBadge } from '../../models/badge';
import type { ProductPageQuery } from '@/shared/apollo-api/__generated__/types';
import { CurrencyFormatter } from '@/components';
import { useProductPage } from '../../business/use-product-page';

// TODO: Replace with actual composables when available
// import { useSearch } from '@/shared/store/business/composable/search';
// import { useTapBar } from '@/features/tapbar/composable/tapBar';

// Temporary mock composables
const useSearch = () => ({ isSearchOpened: ref(false) });
const useTapBar = () => ({ isAnyModalShown: ref(false) });

interface Props {
  sellPrice: number;
  fullPrice?: number;
  badges?: IBadge[];
  discountTimer?: NonNullable<ProductPageQuery['product']['skuList'][0]['discountTimer']>;
  availableAmount: number;
}

const props = withDefaults(defineProps<Props>(), {
  fullPrice: undefined,
  badges: () => [],
  discountTimer: undefined,
});

// Get product page state
const { state } = useProductPage();

const cartAmount = computed(() => state.currentSkuCartCountRef || 0);
const isShown = ref(false);

const { isSearchOpened } = useSearch();
const { isAnyModalShown } = useTapBar();
const hidePriceBlock = computed(() => {
  return isSearchOpened.value || isAnyModalShown.value;
});

const computedSellPrice = computed(() => {
  return props.sellPrice;
});

const computedFullPrice = computed(() => {
  return props.fullPrice || 0;
});

const showSell = computed(() => {
  return props.fullPrice ? props.fullPrice !== props.sellPrice : false;
});

onMounted(() => {
  setTimeout(() => (isShown.value = true), 1000);
});
</script>

<template>
  <transition name="slide">
    <div v-if="isShown && !hidePriceBlock" class="price-block">
      <MMContainer class="price-block-wrapper">
        <div class="price-wrap">
          <div v-if="cartAmount > 0" class="subheader" data-test-id="text__cart-amount">
            {{ `В корзине ${cartAmount} шт.` }}
          </div>
          <div v-else class="subheader strikethrough">
            <CurrencyFormatter
              v-if="showSell"
              class="subheader strikethrough"
              :value="computedFullPrice"
              data-test-id="text__old-price-mobile"
            />
          </div>
          <div class="price">
            <CurrencyFormatter
              class="price"
              :value="computedSellPrice"
              data-test-id="text__product-price-mobile"
            />
          </div>
        </div>
        <AddCart class="add-cart" :availableAmount="availableAmount" />
      </MMContainer>
    </div>
  </transition>
</template>

<style lang="stylus" scoped>
.price-block
  // position, bottom, width и z-index контролируются bottom-bar
  // так как компонент телепортируется в #bottom-bar-additional
  height 60px
  color #000
  background-color #fff
  border-bottom 1px solid #e5e5e5
  transform translateY(0)
  display flex
  align-items center

  &.fade-enter, &.fade-leave-to
    opacity 0

  .price-block-wrapper
    display grid
    gap 12px
    grid-template-columns 1fr 1fr
    align-items center

  .price-wrap
    flex-grow 1
    .subheader
      font-size 12px
      line-height 14px
      color #888

      &.strikethrough
        text-decoration line-through

    .price
      font-size 16px
      line-height 20px
      font-weight 500

.slide-enter-active, .slide-leave-active
  transition transform 0.5s ease-in-out

.slide-enter, .slide-leave-to
  transform translateY(100%)
</style>
